"use client";

import { useState } from "react";
import cls from "./BlogTableOfContents.module.css";
import { MeetOurExpertsCorner } from "@/utils/images/MeetOurExperts";
import { ChevronDownIcon } from "@/utils/images/Blog";

export interface TocItem {
    id: string;
    text: string;
}

interface BlogTableOfContentsProps {
    items: TocItem[];
    activeId?: string;
    /** Reading progress of the article, 0–100. Not shown in the collapsible variant. */
    progress?: number;
    title?: string;
    /** Accordion variant used on phones: header toggles the list, no folded corner. */
    collapsible?: boolean;
    className?: string;
}

export const BlogTableOfContents = ({
    items,
    activeId,
    progress = 0,
    title = "Table of contents",
    collapsible = false,
    className = "",
}: BlogTableOfContentsProps) => {
    const [isOpen, setIsOpen] = useState(false);

    if (items.length === 0) return null;

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();
        window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - 120,
            behavior: "smooth",
        });
        window.history.replaceState(null, "", `#${id}`);
        if (collapsible) setIsOpen(false);
    };

    const list = (
        <ol className={cls.list}>
            {items.map(({ id, text }, index) => (
                <li key={id} className={cls.listItem}>
                    <span className={cls.index}>{index + 1}.</span>
                    <a
                        href={`#${id}`}
                        onClick={(event) => handleClick(event, id)}
                        className={`${cls.link} ${activeId === id ? cls.active : ""}`}
                    >
                        {text}
                    </a>
                </li>
            ))}
        </ol>
    );

    if (collapsible) {
        return (
            <nav className={`${cls.tocCard} ${cls.collapsible} ${className}`} aria-label="Table of contents">
                <div className={cls.content}>
                    <button
                        type="button"
                        className={cls.toggle}
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                    >
                        <span className={cls.title}>{title}</span>
                        <span className={`${cls.icon} ${isOpen ? cls.iconOpen : ""}`}>
                            <ChevronDownIcon style={{ width: "24px", height: "24px" }} />
                        </span>
                    </button>

                    <div className={`${cls.listWrapper} ${isOpen ? cls.listOpen : ""}`}>
                        {list}
                    </div>
                </div>
            </nav>
        );
    }

    return (
        <nav className={`${cls.tocCard} ${className}`} aria-label="Table of contents">
            <div className={cls.corner}><MeetOurExpertsCorner /></div>

            <div className={cls.content}>
                <p className={cls.title}>{title}</p>

                <div
                    className={cls.progressTrack}
                    role="progressbar"
                    aria-label="Reading progress"
                    aria-valuenow={Math.round(progress)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                >
                    <div className={cls.progressBar} style={{ width: `${progress}%` }} />
                </div>

                {list}
            </div>
        </nav>
    );
};
